import { Alert, Typography } from '@compass/components'
import { cn } from '@compass/utils'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { Row } from '@tanstack/react-table'
import { AppRoute, buildRoute } from 'appRoutes'
import clsx from 'clsx'
import Loader from 'components/Loader/Loader'
import { EventsTable } from 'components/Table/EventsTable'
import { LimitSelector } from 'components/Table/LimitSelector'
import { TablePagination } from 'components/Table/Table'
import { TableColumnSelector } from 'components/Table/TableColumnSelector'
import { DOCS_EVADING_ADBLOCKERS, GenericError } from 'const'
import { GenericEmptyState, GenericEmptyStateProps } from 'features/commonUI'
import { useCurrentSubscription, useCurrentSubscriptionData } from 'features/subscription'
import { DEFAULT_STORED_DATA_TTL_DAYS, EVENTS_DATE_RANGE_IDS, getLargestTtlBoundRange } from 'helpers/dateRange'
import { COLUMN_ID_TO_APP_PRODUCT } from 'helpers/smartSignalMappings'
import { useSmartSignalsEnabled } from 'hooks/api/smart_signals'
import { useUserStats } from 'hooks/api/userStats'
import { TOTAL_HITS_MAX_LIMIT } from 'hooks/events'
import { usePaginationForQuery } from 'hooks/usePaginationForQuery'
import { canViewBillingPermission, useAccountPermission } from 'hooks/usePermission'
import { ArrowUpRight, ExternalLink, Meh, Search, SearchX, X } from 'lucide-react'
import { SubscriptionType } from 'models'
import { ampli } from 'models/ampli'
import { IdEventsResponseV4, IdEventV4 } from 'models/event'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import type { Selection } from 'react-aria-components'

import { IntegrationStep } from '../../../../models/integrationStatus'
import { useFilterSearchParams } from '../../hooks/useFilterSearchParams'
import { EventsTableActionsProvider } from './columns/EventsTableActions'
import { RoutesWithEventSearchSchema } from './columns/RoutesWithEventSearchSchema'
import { useVisitsTable } from './useVisitsTable'

export interface VisitsTableProps {
  query: UseInfiniteQueryResult<InfiniteData<IdEventsResponseV4>, GenericError>
  emptyAlert?: string
  selectedRequestId?: string
  withContainer?: boolean
  error?: GenericError | null
  className?: string
  onClear?: () => void
  onLimitChange: (limit: number) => void
  appliedFilterCount?: number
  renderSelectionActions?: (props: VisitsTableSelectionActionsProps) => ReactNode
  /**
   * Identifies the result set the selection belongs to. The selection survives pagination and page-size
   * changes, and is dropped when this changes (filters, date range, workspace).
   */
  selectionScope?: string
}

export interface VisitsTableSelectionActionsProps {
  /** Selected events across every page visited, in the order they were selected. */
  selectedEventIds: string[]
  /** Every event on the loaded page. */
  pageEventIds: string[]
  /** Whether the table is showing rows, rather than loading for the first time or an error. */
  isReady: boolean
  /** Replaces the whole selection, across pages. */
  onSelectionChange: (eventIds: string[]) => void
}

const NO_KEYS: ReadonlySet<string> = new Set()

export function VisitsTableNew({
  query,
  selectedRequestId,
  error,
  className,
  onClear,
  onLimitChange,
  appliedFilterCount = 0,
  renderSelectionActions,
  selectionScope = '',
}: VisitsTableProps) {
  const { data, isFetching } = query
  const {
    values: events,
    currentPageNumber,
    hasPreviousEntries,
    goToPreviousPage,
    hasNextEntries,
    goToNextPage,
  } = usePaginationForQuery(query, (page) => page.events)

  const pages = data?.pages

  const shownItemsStart = pages ? (pages[0]?.events.length ?? 0) * currentPageNumber + 1 : undefined
  const shownItemsEnd =
    shownItemsStart !== undefined && events !== undefined ? shownItemsStart + events.length - 1 : undefined
  const totalHits = pages?.[0]?.totalHits
  const totalHitsIsNotExact = totalHits == null || totalHits === TOTAL_HITS_MAX_LIMIT
  const lastPageIsFull = pages?.[0]?.events.length === events?.length
  const largestValueOnScreen = Math.max(totalHits ?? 0, shownItemsEnd ?? 0)
  const totalItems =
    totalHits === undefined
      ? undefined
      : `${largestValueOnScreen}${totalHitsIsNotExact && lastPageIsFull && hasNextEntries ? '+' : ''}`
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const { subscription } = useCurrentSubscriptionData()
  const hasMadeRequests = subscription?.integrationStep !== IntegrationStep.ApiCalls
  const noResult = useNoResultAction({
    appliedFilterCount,
    onClear,
  })

  const { table, columnVisibility, setColumnVisibility, columnOrder, setColumnOrder, disabledByFeatureFlag } =
    useVisitsTable(subscriptionId, events)
  const router = useRouter()

  const isSelectable = Boolean(renderSelectionActions) && events !== undefined && !error
  const isSelectionReady = isSelectable && !query.isError
  const [selection, setSelection] = useState<{ scope: string; ids: ReadonlySet<string> }>({
    scope: selectionScope,
    ids: NO_KEYS,
  })
  const selectedIds = selection.scope === selectionScope ? selection.ids : NO_KEYS
  const selectedEventIds = useMemo(() => Array.from(selectedIds), [selectedIds])
  const pageEventIds = useMemo(() => events?.map((event) => event.event_id) ?? [], [events])
  // React Aria only sees this page's part of the selection, so select-all and its indeterminate state are per page.
  const pageSelectedKeys = useMemo(
    () => new Set(pageEventIds.filter((eventId) => selectedIds.has(eventId))),
    [pageEventIds, selectedIds]
  )
  const selectEvents = useCallback(
    (eventIds: Iterable<string>) => setSelection({ scope: selectionScope, ids: new Set(eventIds) }),
    [selectionScope]
  )
  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      // Rows kept on screen while another page or page size loads are not the page being selected from.
      if (!isSelectionReady || query.isPlaceholderData) {
        return
      }
      const pageIds = new Set(pageEventIds)
      const pageSelection = new Set(keys === 'all' ? pageEventIds : Array.from(keys, String))
      // Keep the existing order, dropping this page's deselected rows and appending its newly selected ones.
      const kept = selectedEventIds.filter((eventId) => !pageIds.has(eventId) || pageSelection.has(eventId))
      selectEvents([...kept, ...[...pageSelection].filter((eventId) => !selectedIds.has(eventId))])
    },
    [isSelectionReady, query.isPlaceholderData, pageEventIds, selectedEventIds, selectedIds, selectEvents]
  )

  // Memoize dependencies array to prevent React Aria Collection from invalidating
  // its entire cache on every render (it uses reference equality like useMemo)
  const tableDependencies = useMemo(() => [columnVisibility, columnOrder], [columnVisibility, columnOrder])

  // Memoize context value to prevent unnecessary re-renders of all table cells
  const tableActions = useMemo(
    () => ({ from: AppRoute.IdentificationEvents as RoutesWithEventSearchSchema['fullPath'] }),
    []
  )

  const { data: enabledProducts } = useSmartSignalsEnabled(subscriptionId)
  const disabledColumnIds = useMemo(() => {
    if (!enabledProducts) {
      return new Set<string>()
    }
    const enabledSet = new Set(enabledProducts)
    return new Set(
      Object.entries(COLUMN_ID_TO_APP_PRODUCT)
        .filter(([, product]) => !enabledSet.has(product))
        .map(([columnId]) => columnId)
    )
  }, [enabledProducts])

  return (
    <Paper className={clsx('p-0 overflow-hidden relative', className)}>
      {query.isFetching ? <Loader testId='visits-table-loader' className='inset-0' /> : null}
      <div className='overflow-x-auto overscroll-x-none'>
        <div className='sticky left-0 flex gap-2 justify-between items-center px-4 py-3 border-b text-left'>
          {query.data == null && query.isFetching ? (
            <Skeleton width={160} height={16} className='transform-none' />
          ) : (
            <Typography size='base' weight='medium'>
              {largestValueOnScreen > 0 ? (
                <>
                  {totalItems} {Number(totalItems) === 1 ? 'event' : 'events'} matching
                </>
              ) : (
                'No events matching'
              )}
            </Typography>
          )}
          <TableColumnSelector
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
            columns={table.getAllLeafColumns().filter((col) => !disabledByFeatureFlag.has(col.id))}
            disabledColumnIds={disabledColumnIds}
            onColumnAdded={() => ampli.identificationEventsDisplayColumnAdded()}
            onColumnRemoved={() => ampli.identificationEventsDisplayColumnRemoved()}
          />
        </div>

        <EventsTableActionsProvider value={tableActions}>
          <EventsTable
            table={table}
            ariaLabel='Identification events table'
            className='relative'
            dependencies={tableDependencies}
            rows={error ? [] : table.getRowModel().rows}
            selection={
              isSelectable ? { selectedKeys: pageSelectedKeys, onSelectionChange: handleSelectionChange } : undefined
            }
            renderEmptyState={() =>
              error ? (
                <Alert variant='error' className='m-2'>
                  {error?.message ?? 'There was an error getting the events.'}
                </Alert>
              ) : hasMadeRequests ? (
                <GenericEmptyState
                  icon={<SearchX />}
                  title='No results were found'
                  description={
                    noResult.description ??
                    'Try expanding the date range, adjusting any filters, or perhaps take a coffee break.'
                  }
                  buttonProps={noResult.buttonProps}
                />
              ) : (
                <GenericEmptyState
                  icon={<Meh />}
                  title="There's nothing here"
                  description='Events will appear here once your app is sending requests.'
                  buttonProps={{
                    children: 'Install Fingerprint now',
                    variant: 'primary',
                    href: buildRoute(AppRoute.Integrations, { subscriptionId }),
                  }}
                />
              )
            }
            rowProps={(row: Row<IdEventV4>) => {
              const event = row.original
              const isSelected = selectedRequestId === event.event_id

              const route = isSelected
                ? undefined
                : buildRoute(AppRoute.IdentificationEventDetails, {
                    subscriptionId,
                    requestId: event.event_id,
                  })

              return {
                id: row.id,
                className: 'border-t relative',
                highlightSelection: isSelectable,
                href: route,
                onHoverStart: () =>
                  router.preloadRoute({
                    to: route,
                  }),
              }
            }}
          />
        </EventsTableActionsProvider>

        {largestValueOnScreen > 0 && (
          <div className='sticky left-0 flex justify-between gap-4 h-12 px-4 border-t'>
            <div className='flex items-center gap-2'>
              <Typography size='sm' className='text-gray-800'>
                Show
              </Typography>
              <LimitSelector onLimitChange={onLimitChange} />
            </div>
            <div className='flex flex-row items-center justify-end gap-4'>
              {shownItemsEnd ? (
                <Typography size='sm' className='text-gray-800'>
                  {totalItems === undefined ? (
                    <>
                      {shownItemsStart}-{shownItemsEnd} events
                    </>
                  ) : (
                    <>
                      {shownItemsStart}-{shownItemsEnd} of {totalItems} events
                    </>
                  )}
                </Typography>
              ) : null}
              {!error && (
                <TablePagination
                  hasNextEntries={hasNextEntries}
                  hasPreviousEntries={hasPreviousEntries}
                  onRequestNextEntries={goToNextPage}
                  onRequestPreviousEntries={goToPreviousPage}
                  isLoading={isFetching}
                  className={cn('m-0 p-0 gap-0')}
                />
              )}
            </div>
          </div>
        )}
      </div>
      {renderSelectionActions?.({
        selectedEventIds,
        pageEventIds,
        isReady: isSelectionReady,
        onSelectionChange: selectEvents,
      })}
    </Paper>
  )
}

type NoResultActionProps = {
  appliedFilterCount: number
  onClear?: () => void
}

type NoResultAction = Pick<GenericEmptyStateProps, 'description' | 'buttonProps'>

function useNoResultAction({ appliedFilterCount, onClear }: NoResultActionProps): NoResultAction {
  const { data } = useUserStats()
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const { subscription } = useCurrentSubscriptionData()

  const { adaptedFilterValues, setSearchParams } = useFilterSearchParams()
  // Length of the selected range, not its age: a narrow range far back can still be expanded.
  const dateRangeInDays = adaptedFilterValues.before.diff(adaptedFilterValues.since, 'days').days
  const has100ApiCalls = data?.customer.milestones.hundredApiCalls ?? false
  const hasValidSubdomain = data?.customer.subdomains.hasSubdomain ?? false

  const storedDataTtlDays = subscription?.storedDataTtl ?? DEFAULT_STORED_DATA_TTL_DAYS
  const largestRange = getLargestTtlBoundRange(storedDataTtlDays, EVENTS_DATE_RANGE_IDS)
  const isFreemium = subscription?.type === SubscriptionType.Freemium
  // Members without billing access are redirected away from Plans, so don't offer the upgrade link.
  const canViewPlans = useAccountPermission(canViewBillingPermission)

  if (largestRange && adaptedFilterValues.period !== largestRange.id && dateRangeInDays < largestRange.days - 1) {
    return {
      buttonProps: {
        onPress: () => {
          setSearchParams({
            period: largestRange.id,
          })
        },
        variant: 'secondary',
        children: (
          <>
            <Search /> Expand search to {largestRange.days} days
          </>
        ),
      },
    }
  } else if (isFreemium && appliedFilterCount === 0) {
    return {
      description: `The Free plan lets you look up events from the last ${storedDataTtlDays} days.${canViewPlans ? ' Upgrade to search further back.' : ''}`,
      buttonProps: canViewPlans
        ? {
            href: buildRoute(AppRoute.Plans, { subscriptionId }),
            variant: 'secondary',
            children: (
              <>
                Upgrade plan <ArrowUpRight />
              </>
            ),
          }
        : undefined,
    }
  } else if (appliedFilterCount > 0) {
    return {
      buttonProps: {
        onPress: () => {
          onClear?.()
        },
        variant: 'secondary',
        children: (
          <>
            <X /> Clear filters
          </>
        ),
      },
    }
  } else if (!has100ApiCalls && !hasValidSubdomain) {
    return {
      buttonProps: {
        onPress: () => {
          window.open(DOCS_EVADING_ADBLOCKERS, '_blank')
        },
        variant: 'secondary',
        children: (
          <>
            Ad blockers may prevent events from sending
            <ExternalLink />
          </>
        ),
      },
    }
  }

  return {}
}
